// Globals
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { deleteCategory, getCategories, getCategoryStats, getSubCategories, toggleActiveCategory } from '../../feature';
import { categoriesSelector, selectCategoryStatsQuery } from '../../feature/selectors';

// Components
import { Button, Drawer, Skeleton } from '@/ui';
import { EditIcon, PlusCircleIcon } from '@/icons';
import { StyledPageTitle, StyledTabTitleWrapper, StyledButtonWrapper, StyledSection } from '../../components/Styled';
import { CategoryItem, CategoryStatsPanel, DeleteModal } from '../../components';
import EditCategoriesPositionsContainer from '../EditCategoriesPositionsContainer/EditCategoriesPositionsContainer';
import { showErrorToast, showSuccessToast, useForm } from '@/components';
import CreateCategoryContainer from '../CreateCategoryContainer/CreateCategoryContainer';

// Models
import { IApiError } from '@/models/apiError.model';
import { ICategory } from '@/models/categories.model';

// Helpers
import { useBoolean, useModal } from '@/hooks';
import { Resource, usePermissions } from '@/modules/Permissions';

// Styles
import { StyledCategoryList, StyledStatsSidebar, StyledTabLayout } from './CategoriesTabContainerStyles';

const CategoriesTabContainer = () => {
  const { t } = useTranslation(['categories']);

  const dispatch = useAppDispatch();
  const { canManage } = usePermissions();
  const canManageCategories = canManage(Resource.CATEGORY);
  const { categories, isLoadingCategories } = useAppState(categoriesSelector);
  const categoryStatsQuery = useAppState(selectCategoryStatsQuery);

  const [isEdit, setIsEdit] = useState(false);

  const { isOpened, openModal, closeModal } = useModal();
  const { isOpened: isHideModalOpen, openModal: openHideModal, closeModal: closeHideModal } = useModal();
  const [selectedCategory, setSelectedCategory] = useState<ICategory | null>(null);

  const { value: createFormOpened, setFalse: closeCreateForm, setTrue: openCreateForm } = useBoolean();
  const [form] = useForm();

  const handleDrawerClose = () => {
    closeCreateForm();
    form.resetFields();
  };

  const changeCategoryForDelete = (category: ICategory) => {
    setSelectedCategory(category);
    openModal();
  };

  const changeCategoryForHide = (category: ICategory) => {
    setSelectedCategory(category);
    openHideModal();
  };

  const handleDelete = async () => {
    try {
      if (selectedCategory) {
        await dispatch(deleteCategory(selectedCategory.id)).unwrap();
        showSuccessToast({ message: t('delete_category_success') });
        setSelectedCategory(null);
        dispatch(getCategoryStats(categoryStatsQuery));
      }
      closeModal();
    } catch (error) {
      const apiError = error as IApiError;
      showErrorToast({ message: apiError?.message || t('delete_category_error') });
    }
  };

  const handleHide = async () => {
    try {
      if (selectedCategory) {
        await dispatch(toggleActiveCategory(selectedCategory.id)).unwrap();
        showSuccessToast({ message: t('hide_category_success') });
        setSelectedCategory(null);
        dispatch(getCategories());
        dispatch(getCategoryStats(categoryStatsQuery));
      }
      closeHideModal();
    } catch {}
  };

  const onClose = () => {
    closeModal();
    closeHideModal();
    setSelectedCategory(null);
  };

  const handleSaveEditedPositions = () => {
    setIsEdit(false);
    dispatch(getCategories());
  };

  const getSubCategoriesHandler = (id: string) => {
    dispatch(getSubCategories(id));
  };

  useEffect(() => {
    dispatch(getCategories());
  }, []);

  const getContent = () => {
    if (isLoadingCategories) return <Skeleton active />;

    if (isEdit) return <EditCategoriesPositionsContainer />;

    return categories?.map((category) => (
      <StyledSection key={category.id}>
        <CategoryItem
          category={category}
          level={0}
          canManage={canManageCategories}
          onGetSubCategories={getSubCategoriesHandler}
          onDelete={changeCategoryForDelete}
          onHide={changeCategoryForHide}
        />
      </StyledSection>
    ));
  };

  return (
    <>
      <StyledTabTitleWrapper>
        <StyledPageTitle>{t('categories_tab_title')}</StyledPageTitle>

        {canManageCategories && (
          <StyledButtonWrapper>
            {!isEdit && (
              <>
                <Button onClick={openCreateForm} icon={<PlusCircleIcon style={{ maxWidth: '2rem', fill: '#000' }} />}>
                  {t('create_new_category')}
                </Button>
                {(categories?.length || 0) > 0 && (
                  <Button onClick={() => setIsEdit(true)} icon={<EditIcon />}>
                    {t('edit_categories')}
                  </Button>
                )}
              </>
            )}

            {isEdit && <Button onClick={handleSaveEditedPositions}>{t('save_edited_categories')}</Button>}
          </StyledButtonWrapper>
        )}
      </StyledTabTitleWrapper>

      <StyledTabLayout>
        <StyledCategoryList>{getContent()}</StyledCategoryList>
        <StyledStatsSidebar>
          <CategoryStatsPanel />
        </StyledStatsSidebar>
      </StyledTabLayout>

      <DeleteModal
        isOpen={isOpened}
        isLoading={isLoadingCategories}
        onClose={onClose}
        onConfirm={handleDelete}
        title={t('delete_category_title', {
          name: selectedCategory?.name,
        })}
        description={t('delete_category_description')}
        closeBtnText={t('delete_category_back')}
        confirmBtnText={t('delete_category_confirm')}
      />

      <DeleteModal
        isOpen={isHideModalOpen}
        isLoading={isLoadingCategories}
        onClose={onClose}
        onConfirm={handleHide}
        title={t(!selectedCategory?.active ? 'activate_category_title' : 'hide_category_title', {
          name: selectedCategory?.name,
        })}
        description={t(!selectedCategory?.active ? 'activate_category_text' : 'hide_category_text', {
          name: selectedCategory?.name,
        })}
        closeBtnText={t('delete_category_back')}
        confirmBtnText={t(!selectedCategory?.active ? 'hide_category_show' : 'hide_category_confirm')}
      />

      <Drawer open={createFormOpened} onClose={handleDrawerClose}>
        {createFormOpened && <CreateCategoryContainer form={form} onCreate={handleDrawerClose} />}
      </Drawer>
    </>
  );
};

export default CategoriesTabContainer;
